import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { http, eur, errMsg, imgSrc } from "@/lib/api";
import { ProductForm } from "./ProductForm";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const ProductsManager = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    http.get("/admin/products").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
    http.get("/admin/featured").then((r) => setFeatured(r.data?.featured_product_id ?? null)).catch(() => null);
    http.get("/categories").then((r) => setCats(Array.isArray(r.data) ? r.data : [])).catch(() => null);
  }, []);
  useEffect(() => { load(); }, [load]);
  const labelOf = (id) => cats.find((c) => c.id === id)?.label || id;
  const shown = filter === "all" ? rows : rows.filter((p) => p.category === filter);

  const feature = async (p) => {
    try {
      await http.put(`/admin/featured/${p.id}`);
      setFeatured(p.id);
      toast.success(`${p.name} ora è in evidenza in home`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    try {
      await http.delete(`/admin/products/${removing.id}`);
      toast.success("Prodotto rimosso dal negozio");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setRemoving(null);
  };

  return (
    <div className="mt-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button onClick={() => setEditing({})} data-testid="admin-product-new-button"
          className="flex items-center gap-2 rounded-full bg-[var(--volt)] px-5 py-3 text-sm font-semibold text-[var(--ink)] active:scale-95">
          <Plus className="h-4 w-4" /> Nuovo prodotto
        </button>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-products-category-filter"
          className="rounded-full border border-fg/20 bg-[var(--ink-2)] px-4 py-3 text-sm text-fg">
          <option value="all">Tutte le categorie ({rows.length})</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.label} ({rows.filter((p) => p.category === c.id).length})</option>)}
        </select>
      </div>
      <div className="grid gap-3" data-testid="admin-products-list">
        {shown.map((p) => (
          <article key={p.id} data-testid={`admin-product-${p.id}`} className="flex items-center gap-4 rounded-3xl border border-fg/10 bg-[var(--ink-2)] p-3 pr-4">
            <img src={imgSrc(p.image)} alt={p.name} className="h-16 w-16 shrink-0 rounded-2xl object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg">{p.name}</p>
              <p className="text-xs text-mist">{labelOf(p.category)} · {p.stock} pz{featured === p.id && <span className="ml-2 rounded-full bg-[var(--volt)] px-2 py-0.5 text-[var(--ink)]" data-testid={`admin-product-featured-badge-${p.id}`}>In evidenza</span>}</p>
            </div>
            <p className="hidden font-display text-xl sm:block">{eur(p.price)}</p>
            <button onClick={() => feature(p)} data-testid={`admin-product-feature-${p.id}`} aria-label="Metti in evidenza" title="Metti in evidenza in home"
              className={`flex h-10 w-10 items-center justify-center rounded-full hover:bg-fg/10 ${featured === p.id ? "text-[var(--volt)]" : "text-mist"}`}>
              <Star className={`h-4 w-4 ${featured === p.id ? "fill-current" : ""}`} />
            </button>
            <button onClick={() => setEditing(p)} data-testid={`admin-product-edit-${p.id}`} aria-label="Modifica" className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-fg/10"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => setRemoving(p)} data-testid={`admin-product-delete-${p.id}`} aria-label="Elimina" className="flex h-10 w-10 items-center justify-center rounded-full text-[var(--flare)] hover:bg-fg/10"><Trash2 className="h-4 w-4" /></button>
          </article>
        ))}
      </div>
      <ProductForm product={editing} categories={cats} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent className="border-fg/10 bg-[var(--ink-2)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {removing?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Il prodotto sparirà dal negozio. Gli ordini già fatti restano nello storico.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-product-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="admin-product-delete-confirm" className="bg-[var(--flare)] text-fg hover:bg-[var(--flare)]/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
